import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import LayoutWithNavbar from './LayoutWithNavbar';
import {
  fetchStates,
  fetchSupplierTypes,
  fetchCumm2BDetails
} from '../services/api';
import { FaFileExcel } from 'react-icons/fa';
import * as XLSX from 'xlsx';
import { saveAs } from 'file-saver';

const All2BPage = () => {
  const [states, setStates] = useState([]);
  const [selectedState, setSelectedState] = useState('');
  const [selectedMonth, setSelectedMonth] = useState('');
  const [selectedType, setSelectedType] = useState('');
  const [gstin, setGstin] = useState('');
  const [summaryData, setSummaryData] = useState([]);
  const [supplierTypes, setSupplierTypes] = useState([]);
  const [loading, setLoading] = useState(false);
  const [editModal, setEditModal] = useState({ open: false, gstid: null, selectedType: '' });
  const navigate = useNavigate();

  useEffect(() => {
    const loadInitData = async () => {
      const stateRes = await fetchStates();
      if (stateRes.success) setStates(stateRes.data);

      const supplierRes = await fetchSupplierTypes();
      if (supplierRes.success) setSupplierTypes(supplierRes.data);
    };
    loadInitData();
  }, []);

  const handleSearch = async () => {
    setLoading(true);
    const filters = {
      state: selectedState,
      month: selectedMonth,
      gstin: gstin,
      type: selectedType
    };
    const res = await fetchCumm2BDetails(filters);
    if (res.success) setSummaryData(res.data.summary_by_vendor || []);
    else alert(res.message);
    setLoading(false);
  };

  const openEditModal = (gstid, currentTypeName) => {
    const typeObj = supplierTypes.find(t => t.suppliertype === currentTypeName);
    setEditModal({ open: true, gstid, selectedType: typeObj?.id || '' });
  };

  const closeModal = () => setEditModal({ open: false, gstid: null, selectedType: '' });

  const handleModalChange = (e) =>
    setEditModal(prev => ({ ...prev, selectedType: e.target.value }));

  const handleExport = () => {
    if (!summaryData.length) return alert("No data to export");

    const exportData = summaryData.map(row => ({
      COMPANY: row.company_name,
      GSTIN: row.gstin,
      VENDOR_TYPE: row.vendor_type,
      REF_NO: row.ref_no,
      INVOICE_DATE: row.invoice_date,
      TAXABLE_VALUE: row.taxable_value,
      CGST: row.cgst,
      SGST: row.sgst,
      IGST: row.igst,
      NET_AMOUNT: row.net_amount,
      MONTH: row.month,
      BRANCH: row.branch,
      OVERDUE_DAYS: row.overdue_days
    }));

    const worksheet = XLSX.utils.json_to_sheet(exportData);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "2B Details - Invoicewise");

    const excelBuffer = XLSX.write(workbook, { bookType: "xlsx", type: "array" });
    const data = new Blob([excelBuffer], { type: "application/octet-stream" });
    saveAs(data, "2B_Details.xlsx");
  };

  return (
    <LayoutWithNavbar>
      <div style={styles.container}>
        <h2 style={styles.heading}>UNReconciled 2B</h2>

        <div style={styles.filterSection}>
          <select value={selectedState} onChange={e => setSelectedState(e.target.value)} style={styles.select}>
            <option value=''>Select State</option>
            {states.map(s => <option key={s.id} value={s.id}>{s.statename}</option>)}
          </select>

          <select value={selectedType} onChange={e => setSelectedType(e.target.value)} style={styles.select}>
            <option value=''>Select Vendor Type</option>
            {supplierTypes.map(type => (
              <option key={type.id} value={type.suppliertype}>{type.suppliertype}</option>
            ))}
          </select>

          <input type="text" placeholder="GSTIN No" value={gstin} onChange={e => setGstin(e.target.value)} style={styles.input} />
          <button onClick={handleSearch} style={styles.searchButton}>Search</button>
          <button onClick={handleExport} style={styles.exportButton}>
            <FaFileExcel style={{ marginRight: '8px' }} /> Export
          </button>
        </div>

        {loading ? (
          <div style={styles.loaderWrapper}><div className="spinner"></div><p>Loading data...</p></div>
        ) : (
          <div style={styles.tableWrapper}>
            <table style={styles.table}>
              <thead>
                <tr>
                  {['Company','GSTIN','VendorType','Branch', 'Ref No',  'Invoice Date', 'Taxable Value', 'CGST', 'SGST', 'IGST', 'Net Amount', 'Month',  'Overdue Days'].map((col, i) => (
                    <th key={i} style={styles.th}>{col}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
              {summaryData.map((inv, i) => (
                <tr key={i} style={styles.tr}>
                  <td style={styles.td}>{inv.company_name}</td>
                  <td style={styles.td}>{inv.gstin}</td>
                  <td style={styles.td}>{inv.vendor_type}</td>
                  <td style={styles.td}>{inv.branch}</td>
                  <td style={styles.td}>{inv.ref_no}</td>
                  <td style={styles.td}>{inv.invoice_date}</td>
                  <td style={styles.td}>{inv.taxable_value}</td>
                  <td style={styles.td}>{inv.cgst}</td>
                  <td style={styles.td}>{inv.sgst}</td>
                  <td style={styles.td}>{inv.igst}</td>
                  <td style={styles.td}>{inv.net_amount}</td>
                  <td style={styles.td}>{inv.month}</td>                  
                  <td style={styles.td}>{inv.overdue_days}</td>
                </tr>
              ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </LayoutWithNavbar>
  );
};

const styles = {
  container: { padding: '30px', backgroundColor: '#f8f9fa', minHeight: '100vh', fontFamily: 'Segoe UI, sans-serif' },
  heading: { fontSize: '24px', fontWeight: '600', marginBottom: '20px' },
  filterSection: { display: 'flex', flexWrap: 'wrap', gap: '12px', marginBottom: '25px', backgroundColor: '#fff', padding: '20px', borderRadius: '8px', boxShadow: '0 0 10px rgba(0,0,0,0.05)' },
  select: { padding: '16px', fontSize: '20px', border: '1px solid #ccc', borderRadius: '6px', flex: '1' },
  input: { padding: '10px', fontSize: '16px', border: '1px solid #ccc', borderRadius: '6px', flex: '2' },
  searchButton: { padding: '10px 20px', fontSize: '16px', fontWeight: '600', backgroundColor: '#007bff', color: '#fff', border: 'none', borderRadius: '6px', cursor: 'pointer' },
  exportButton: { padding: '10px 20px', fontSize: '16px', fontWeight: '600', backgroundColor: '#28a745', color: '#fff', border: 'none', borderRadius: '6px', cursor: 'pointer' },
  loaderWrapper: { textAlign: 'center', marginTop: '40px' },
  tableWrapper: { overflowX: 'auto', backgroundColor: '#fff', borderRadius: '8px', boxShadow: '0 0 10px rgba(0,0,0,0.04)' },
  table: { width: '100%', borderCollapse: 'collapse', minWidth: '1200px' },
  th: { padding: '12px', backgroundColor: '#343a40', color: '#fff', fontWeight: '800', textAlign: 'left', borderBottom: '1px solid #dee2e6' },
  tr: { cursor: 'pointer', transition: 'background-color 0.2s' },
  td: { padding: '12px', borderBottom: '1px solid #dee2e6', fontSize: '17px', backgroundColor: '#fff' }
};

export default All2BPage;
